<template>
  <b-modal v-model="modal" :title="title" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true">
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveParticipant()" autocomplete="off">
          <!-- All Fields from Form Builder -->
          <div class="row">
            <div
              v-for="field in formFields"
              :key="field.id"
              :class="getFieldColClass(field)"
            >
              <div class="form-group">
                <label :for="'field_' + field.name">
                  {{ field.label }}
                  <span v-if="field.required" class="text-danger">*</span>
                </label>

                <!-- Password field (special handling for new/edit) -->
                <template v-if="getFieldTypeName(field) === 'password'">
                  <input
                    v-if="!participant.id && setting.access_type === 1"
                    :id="'field_' + field.name"
                    v-model="participant.password"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && $v.participant.password.$error }"
                    :placeholder="field.placeholder"
                  />
                  <input
                    v-else-if="participant.id"
                    :id="'field_' + field.name"
                    v-model="participant.password"
                    type="text"
                    class="form-control"
                    :placeholder="field.placeholder || 'Deixe em branco para manter a senha atual'"
                  />
                  <div v-if="submitted && $v.participant.password && !$v.participant.password.required" class="invalid-feedback">
                    O campo senha é obrigatório.
                  </div>
                </template>

                <!-- State field (multiselect with Brazilian states) -->
                <template v-else-if="getFieldTypeName(field) === 'state'">
                  <multiselect
                    v-model="selectedState"
                    :options="states"
                    :max-height="180"
                    trackBy="value"
                    label="label"
                    :placeholder="field.placeholder || 'Selecione o estado'"
                    @input="onStandardStateSelect"
                  />
                </template>

                <!-- Select field -->
                <template v-else-if="isSelectField(field)">
                  <select
                    :id="'field_' + field.name"
                    :value="getFieldValue(field)"
                    class="form-control"
                    @change="onFieldChange(field, $event.target.value)"
                  >
                    <option value="">{{ field.placeholder || 'Selecione...' }}</option>
                    <option
                      v-for="opt in getFieldOptions(field)"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </template>

                <!-- Multiselect field (multiple options) -->
                <template v-else-if="isMultiselectField(field)">
                  <multiselect
                    v-model="customDataSelects[field.name]"
                    :options="getFieldOptions(field)"
                    :max-height="180"
                    track-by="value"
                    label="label"
                    :multiple="true"
                    :placeholder="field.placeholder || 'Selecione...'"
                    @input="onMultiselectChange(field.name, $event)"
                  />
                </template>

                <!-- Textarea -->
                <template v-else-if="getFieldTypeName(field) === 'textarea'">
                  <textarea
                    :id="'field_' + field.name"
                    :value="getFieldValue(field)"
                    @input="onFieldChange(field, $event.target.value)"
                    :placeholder="field.placeholder"
                    class="form-control"
                    rows="3"
                  ></textarea>
                </template>

                <!-- Radio -->
                <template v-else-if="getFieldTypeName(field) === 'radio'">
                  <div class="mt-2">
                    <div
                      v-for="opt in getFieldOptions(field)"
                      :key="opt.value"
                      class="form-check"
                    >
                      <input
                        :id="'field_' + field.name + '_' + opt.value"
                        :checked="getFieldValue(field) === opt.value"
                        @change="onFieldChange(field, opt.value)"
                        :name="field.name"
                        :value="opt.value"
                        type="radio"
                        class="form-check-input"
                      />
                      <label :for="'field_' + field.name + '_' + opt.value" class="form-check-label">
                        {{ opt.label }}
                      </label>
                    </div>
                  </div>
                </template>

                <!-- Checkbox (multiple) -->
                <template v-else-if="getFieldTypeName(field) === 'checkbox'">
                  <div class="mt-2">
                    <div
                      v-for="opt in getFieldOptions(field)"
                      :key="opt.value"
                      class="form-check"
                    >
                      <input
                        :id="'field_' + field.name + '_' + opt.value"
                        v-model="customDataArrays[field.name]"
                        :value="opt.value"
                        type="checkbox"
                        class="form-check-input"
                        @change="onCheckboxChange(field.name)"
                      />
                      <label :for="'field_' + field.name + '_' + opt.value" class="form-check-label">
                        {{ opt.label }}
                      </label>
                    </div>
                  </div>
                </template>

                <!-- Date fields -->
                <template v-else-if="isDateField(field)">
                  <input
                    :id="'field_' + field.name"
                    :value="getFieldValue(field)"
                    @input="onFieldChange(field, $event.target.value)"
                    type="date"
                    class="form-control"
                  />
                </template>

                <!-- Phone field with mask -->
                <template v-else-if="isPhoneField(field)">
                  <input
                    :id="'field_' + field.name"
                    v-model="participant.phone"
                    v-mask="'(##) #####-####'"
                    type="tel"
                    :placeholder="field.placeholder || '(00) 00000-0000'"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && hasValidationError(field) }"
                  />
                  <div v-if="submitted && hasValidationError(field)" class="invalid-feedback">
                    O campo {{ field.label.toLowerCase() }} é obrigatório.
                  </div>
                </template>

                <!-- Standard text fields (name, email, city) -->
                <template v-else-if="isStandardField(field)">
                  <input
                    :id="'field_' + field.name"
                    v-model="participant[getStandardFieldKey(field)]"
                    :type="getInputType(field)"
                    :placeholder="field.placeholder"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && hasValidationError(field) }"
                  />
                  <div v-if="submitted && hasValidationError(field)" class="invalid-feedback">
                    O campo {{ field.label.toLowerCase() }} é obrigatório.
                  </div>
                </template>

                <!-- Default text input for custom fields -->
                <template v-else>
                  <input
                    :id="'field_' + field.name"
                    v-model="customData[field.name]"
                    :type="getInputType(field)"
                    :placeholder="field.placeholder"
                    class="form-control"
                  />
                </template>

                <small v-if="field.help_text" class="form-text text-muted">
                  {{ field.help_text }}
                </small>
              </div>
            </div>
          </div>

          <!-- Room Permissions -->
          <div class="row mt-3">
            <div class="col-sm-12">
              <h6>
                <i class="bx bx-door-open mr-1"></i>
                Permissões de sala restritas
              </h6>
              <div class="form-group">
                <multiselect
                  tag-placeholder="Selecione uma ou mais salas"
                  placeholder="Selecione uma ou mais salas"
                  v-model="participant.rooms"
                  :options="optionsRooms"
                  class="helo"
                  selectLabel="Click para selecionar"
                  selectedLabel="Selecionado"
                  deselectLabel="Click para remover"
                  label="name"
                  track-by="id"
                  :max-height="180"
                  :multiple="true"
                  open-direction="bottom"
                />
              </div>
            </div>
          </div>

          <!-- Sympla Integration -->
          <div class="row" v-if="participant.sympla">
            <div class="col-sm-12">
              <span class="badge badge-soft-success font-size-11 mb-1">Integração Sympla</span>
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th>Nº Pedido</th>
                    <th>Cód. Ingresso</th>
                    <th>Ingresso</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ participant.sympla_order_id }}</td>
                    <td>{{ participant.sympla_ticket_number }}</td>
                    <td>
                      <span v-if="participant.sympla_ticket_number" class="badge badge-soft-primary font-size-11 mr-1">
                        {{ participant.sympla_ticket_name }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </form>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close()">Cancelar</b-button>
      <button type="submit" class="btn btn-primary mr-1" @click="saveParticipant()">Salvar</button>
    </template>
  </b-modal>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import { states } from './statesData'
import Multiselect from 'vue-multiselect'
import { required } from 'vuelidate/lib/validators'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import roomService from '@/pages/rooms/services'
import participantService from './services'
import { VueMaskDirective } from 'v-mask'

export default {
  components: {
    Loading,
    Multiselect
  },
  directives: {
    mask: VueMaskDirective
  },
  props: {
    idParticipant: {
      type: Number || String,
      default: null
    }
  },
  data () {
    return {
      title: 'Novo Participante',
      loading: true,
      fullPage: true,
      submitted: false,
      states: states,
      optionsRooms: [],
      participant: {},
      modal: false,
      formFields: [],
      customData: {},
      customDataSelects: {},
      customDataArrays: {},
      selectedState: null
    }
  },
  validations () {
    const rules = {
      participant: {
        name: { required },
        email: { required }
      }
    }

    // Add password validation for new participants with access_type 1
    if (!this.participant.id && this.setting.access_type === 1) {
      rules.participant.password = { required }
    }

    return rules
  },
  methods: {
    ...mapActions('event', {
      GetSetting: 'GetSetting'
    }),
    ...mapActions('participant', {
      getParticipant: 'getParticipant',
      postData: 'save'
    }),

    isStandardField (field) {
      // Phone has its own template, exclude it here
      if (this.isPhoneField(field)) {
        return false
      }

      const fieldName = (field.name || '').toLowerCase()
      const typeName = this.getFieldTypeName(field)

      // Standard field names (phone excluded - has its own template)
      const standardNames = ['name', 'email', 'city', 'cidade', 'district', 'estado', 'uf']
      if (standardNames.includes(fieldName)) {
        return true
      }
      // Standard field types (phone excluded - has its own template)
      const standardTypes = ['name', 'email', 'city', 'state']
      if (standardTypes.includes(typeName)) {
        return true
      }
      return false
    },

    getStandardFieldKey (field) {
      const fieldName = (field.name || '').toLowerCase()
      const typeName = this.getFieldTypeName(field)

      // Map field name to participant property
      if (['name'].includes(fieldName)) return 'name'
      if (['email'].includes(fieldName)) return 'email'
      if (['phone', 'telefone'].includes(fieldName)) return 'phone'
      if (['city', 'cidade'].includes(fieldName)) return 'city'
      if (['district', 'estado', 'uf'].includes(fieldName)) return 'district'

      // Map by type
      if (typeName === 'name') return 'name'
      if (typeName === 'email') return 'email'
      if (typeName === 'phone') return 'phone'
      if (typeName === 'city') return 'city'
      if (typeName === 'state') return 'district'

      return field.name
    },

    getFieldValue (field) {
      if (this.isStandardField(field)) {
        const key = this.getStandardFieldKey(field)
        return this.participant[key] || ''
      }
      return this.customData[field.name] || ''
    },

    onFieldChange (field, value) {
      if (this.isStandardField(field)) {
        const key = this.getStandardFieldKey(field)
        this.$set(this.participant, key, value)
      } else {
        this.$set(this.customData, field.name, value)
      }
    },

    hasValidationError (field) {
      const key = this.getStandardFieldKey(field)
      return this.$v.participant[key] && this.$v.participant[key].$error
    },

    // Helper to get field type name (handles Fractal nested data structure)
    getFieldTypeName (field) {
      return field.fieldType?.data?.name ||
             field.fieldType?.name ||
             field.field_type?.data?.name ||
             field.field_type?.name ||
             'text'
    },

    isSelectField (field) {
      return this.getFieldTypeName(field) === 'select'
    },

    isMultiselectField (field) {
      return this.getFieldTypeName(field) === 'multiselect'
    },

    isDateField (field) {
      const dateTypes = ['date', 'birthdate', 'datetime']
      return dateTypes.includes(this.getFieldTypeName(field))
    },

    isPhoneField (field) {
      const fieldName = (field.name || '').toLowerCase()
      const typeName = this.getFieldTypeName(field)
      return fieldName === 'phone' || fieldName === 'telefone' || typeName === 'phone'
    },

    // Helper to get field options (handles both direct and nested format)
    getFieldOptions (field) {
      return field.options?.data || field.options || []
    },

    getInputType (field) {
      const typeName = this.getFieldTypeName(field)
      const typeMap = {
        email: 'email',
        phone: 'tel',
        number: 'number',
        url: 'url'
      }
      return typeMap[typeName] || 'text'
    },

    getFieldColClass (field) {
      const cols = field.layout?.cols || 12
      if (cols === 6) return 'col-sm-6'
      if (cols === 4) return 'col-sm-4'
      if (cols === 3) return 'col-sm-3'
      return 'col-sm-12'
    },

    onStandardStateSelect (selected) {
      this.$set(this.participant, 'district', selected ? selected.value : '')
    },

    onCheckboxChange (fieldName) {
      this.$set(this.customData, fieldName, this.customDataArrays[fieldName].join(','))
    },

    onMultiselectChange (fieldName, selected) {
      // Store as comma-separated values
      const values = selected ? selected.map(opt => opt.value).join(',') : ''
      this.$set(this.customData, fieldName, values)
    },

    async loadFormFields () {
      try {
        const res = await participantService.getFormFields()
        const fields = res.data?.data || []

        // Sort by order and use all fields
        this.formFields = fields.sort((a, b) => (a.order || 0) - (b.order || 0))

        // Initialize data structures for each field
        this.formFields.forEach(field => {
          const typeName = this.getFieldTypeName(field)

          if (!this.isStandardField(field)) {
            this.$set(this.customData, field.name, '')
          }

          if (typeName === 'checkbox') {
            this.$set(this.customDataArrays, field.name, [])
          }

          if (typeName === 'multiselect') {
            this.$set(this.customDataSelects, field.name, [])
          }
        })
      } catch (err) {
        console.error('Error loading form fields:', err)
      }
    },

    populateFieldValues () {
      if (!this.data) return

      // Populate standard fields (already done via participant object)

      // Populate state multiselect if participant has district
      if (this.data.district) {
        const districtValue = String(this.data.district).trim().toUpperCase()
        this.selectedState = this.states.find(s => s.value.toUpperCase() === districtValue) || null
      }

      // Populate custom data from saved values (handle both direct and Fractal nested format)
      let customData = this.data.custom_data?.data || this.data.custom_data

      // If custom_data is empty or not present, check for field values directly on the data object
      if (!customData || Object.keys(customData).length === 0) {
        customData = {}
        // Look for custom field values directly on the data object
        this.formFields.forEach(field => {
          if (!this.isStandardField(field) && this.data[field.name] !== undefined) {
            customData[field.name] = this.data[field.name]
          }
        })
      }

      if (customData && typeof customData === 'object') {
        Object.keys(customData).forEach(fieldName => {
          const value = customData[fieldName]
          this.$set(this.customData, fieldName, value)

          // Find field to check its type
          const field = this.formFields.find(f => f.name === fieldName)
          if (field) {
            const typeName = this.getFieldTypeName(field)

            if (typeName === 'checkbox' && value) {
              const arr = typeof value === 'string' ? value.split(',').map(v => v.trim()) : (Array.isArray(value) ? value : [])
              this.$set(this.customDataArrays, fieldName, arr)
            }

            if (typeName === 'multiselect' && value) {
              let selectedOptions = []
              const options = this.getFieldOptions(field)

              // Try to parse as JSON first (handles legacy format where full objects were saved)
              if (typeof value === 'string' && value.startsWith('[')) {
                try {
                  const parsed = JSON.parse(value)
                  if (Array.isArray(parsed)) {
                    // If parsed array contains objects with value/label, use them directly
                    selectedOptions = parsed.map(item => {
                      if (item && item.value !== undefined) {
                        // Find matching option from field options for consistency
                        const matchingOpt = options.find(opt => String(opt.value) === String(item.value))
                        return matchingOpt || item
                      }
                      // If it's just a value, find the option
                      const matchingOpt = options.find(opt => String(opt.value) === String(item))
                      return matchingOpt
                    }).filter(opt => opt !== undefined)
                  }
                } catch (e) {
                  // Not valid JSON, fall through to comma-separated handling
                }
              }

              // If not parsed as JSON, handle as comma-separated values
              if (selectedOptions.length === 0 && value) {
                const values = typeof value === 'string' ? value.split(',').map(v => v.trim()) : (Array.isArray(value) ? value : [])
                selectedOptions = values
                  .map(v => options.find(opt => String(opt.value).trim() === String(v).trim()))
                  .filter(opt => opt !== undefined)
              }

              this.$set(this.customDataSelects, fieldName, selectedOptions)
            }
          }
        })
      }
    },

    async loadItem (id) {
      if (id) {
        this.title = 'Editar Participante'
        await this.getParticipant(id)

        this.participant = { ...this.data }

        if (this.data.rooms) {
          this.participant.rooms = this.data.rooms.data
        }

        this.populateFieldValues()
        this.loading = false
      }
    },

    loadRooms () {
      roomService.list({ type: 1 }).then((res) => {
        this.optionsRooms = res.data.data
      })
    },

    async open (id = null) {
      this.modal = true
      this.loading = true
      this.resetForm()
      this.loadRooms()
      this.GetSetting()
      await this.loadFormFields()

      if (id) {
        await this.loadItem(id)
        return
      }
      this.loading = false
    },

    resetForm () {
      this.participant = {}
      this.customData = {}
      this.customDataSelects = {}
      this.customDataArrays = {}
      this.selectedState = null
      this.submitted = false
    },

    async saveParticipant () {
      this.submitted = true
      this.$v.participant.$touch()

      if (this.$v.participant.$invalid) {
        window.scroll(0, 0)
        return
      }

      this.loading = true

      // Add custom data to the payload
      this.participant.custom_data = { ...this.customData }

      try {
        await this.postData(this.participant)
        successMessage('Sucesso: Dados armazenados com sucesso.', 2500)
        this.close()
        this.$emit('created')
      } catch (err) {
        errorMessage('Alerta: ' + err.message)
        this.loading = false
      }
    },

    close () {
      this.modal = false
      this.resetForm()
      this.$v.$reset()
    }
  },
  computed: {
    ...mapState('event', ['setting']),
    ...mapState('participant', { data: 'participant' })
  },
  watch: {
    modal (val) {
      if (!val) {
        this.close()
      }
    }
  }
}
</script>

<style scoped>
.vld-overlay.is-full-page {
  z-index: 1051 !important;
}
</style>
